import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatCard } from "../components/shared/StatCard";
import { Spinner } from "../components/shared/Spinner";
import { reportsAPI } from "../services/api";
import { fmt, r1 } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

type Period = "daily" | "weekly" | "monthly" | "yearly";

const PlRow: React.FC<{
	label: string;
	value: number | string;
	cls?: string;
	indent?: boolean;
	bold?: boolean;
	topB?: boolean;
}> = ({ label, value, cls, indent = false, bold = false, topB = false }) => (
	<div className={`pl-row${bold ? " bold" : ""}${topB ? " topb" : ""}`}>
		<span
			style={{
				fontSize: 13,
				fontWeight: bold ? 700 : 500,
				color: "var(--text2)",
				paddingLeft: indent ? 20 : 0,
			}}>
			{label}
		</span>
		<span
			style={{
				fontFamily: bold ? "Syne,sans-serif" : "inherit",
				fontSize: bold ? 15 : 13,
				fontWeight: bold ? 800 : 600,
				color:
					cls === "green"
						? "var(--accent)"
						: cls === "red"
							? "var(--red)"
							: cls === "amber"
								? "var(--amber)"
								: "var(--text)",
			}}>
			{typeof value === "number" ? fmt(value) : value}
		</span>
	</div>
);

export const FinancialPage: React.FC = () => {
	useTitle("Financial Reports");
	const [period, setPeriod] = useState<Period>("monthly");
	const { data, isLoading } = useQuery({
		queryKey: ["financial", period],
		queryFn: () => reportsAPI.financial({ period }),
		retry: false,
	});
	const fin = data?.data || {};
	const inc = fin.incomeStatement || {};
	const tax = fin.tax || {};
	const kpis = fin.kpis || {};
	const opEx = inc.operatingExpenses || {};

	if (isLoading) return <Spinner />;

	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					marginBottom: 20,
					flexWrap: "wrap",
					gap: 12,
				}}>
				<div>
					<div className="page-title">Financial Reports</div>
					<div className="page-sub">
						P&L · Revenue · Cost Analysis · Tax · KWD
					</div>
				</div>
				<div className="filter-tabs" style={{ marginBottom: 0 }}>
					{(["daily", "weekly", "monthly", "yearly"] as Period[]).map((p) => (
						<button
							key={p}
							className={`filter-tab ${period === p ? "active" : ""}`}
							onClick={() => setPeriod(p)}>
							{p.charAt(0).toUpperCase() + p.slice(1)}
						</button>
					))}
				</div>
			</div>

			<div className="stat-grid">
				<StatCard
					label="Net Revenue"
					value={fmt(inc.netRevenue || 0)}
					cls="green"
					icon="💰"
					accent="#06a384"
				/>
				<StatCard
					label="Gross Profit"
					value={fmt(inc.grossProfit || 0)}
					cls="green"
					icon="📈"
					change={`Margin: ${inc.grossMargin || "0%"}`}
					accent="#4ade80"
				/>
				<StatCard
					label="Net Profit"
					value={fmt(inc.netProfit || 0)}
					cls={(inc.netProfit || 0) >= 0 ? "green" : "red"}
					icon="🏆"
					change={`Margin: ${inc.netMargin || "0%"}`}
					accent={(inc.netProfit || 0) >= 0 ? "#06a384" : "#ef4444"}
				/>
				<StatCard
					label="VAT Collected"
					value={fmt(tax.collected || 0)}
					cls="amber"
					icon="🏛️"
					change={`Rate: ${tax.rate || "15%"}`}
					accent="#f59e0b"
				/>
				<StatCard
					label="Total Orders"
					value={kpis.totalOrders || 0}
					icon="📋"
				/>
				<StatCard
					label="Items Sold"
					value={kpis.totalItemsSold || 0}
					icon="🍽️"
				/>
			</div>

			<div className="grid-2">
				{/* P&L Statement */}
				<div className="card">
					<div className="card-title">📋 Profit & Loss Statement</div>
					<div
						style={{
							fontSize: 10,
							fontWeight: 800,
							color: "var(--muted)",
							textTransform: "uppercase",
							letterSpacing: ".5px",
							padding: "0 12px 8px",
						}}>
						INCOME
					</div>
					<PlRow
						label="Gross Revenue"
						value={inc.grossRevenue || 0}
						cls="green"
						bold
					/>
					<PlRow
						label="Discounts Given"
						value={Math.abs(inc.discounts || 0)}
						cls="red"
						indent
					/>
					<PlRow
						label="Net Revenue"
						value={inc.netRevenue || 0}
						cls="green"
						bold
						topB
					/>

					<div style={{ height: 14 }} />
					<div
						style={{
							fontSize: 10,
							fontWeight: 800,
							color: "var(--muted)",
							textTransform: "uppercase",
							letterSpacing: ".5px",
							padding: "0 12px 8px",
						}}>
						COST OF GOODS SOLD
					</div>
					<PlRow
						label="COGS"
						value={Math.abs(inc.cogs || 0)}
						cls="red"
						indent
					/>
					<PlRow
						label="Gross Profit"
						value={inc.grossProfit || 0}
						cls="green"
						bold
						topB
					/>

					<div style={{ height: 14 }} />
					<div
						style={{
							fontSize: 10,
							fontWeight: 800,
							color: "var(--muted)",
							textTransform: "uppercase",
							letterSpacing: ".5px",
							padding: "0 12px 8px",
						}}>
						OPERATING EXPENSES
					</div>
					<PlRow
						label="Staff & Labor"
						value={Math.abs(opEx.staffCost || 0)}
						cls="amber"
						indent
					/>
					<PlRow
						label="Rent & Utilities"
						value={Math.abs(opEx.rent || 0)}
						cls="amber"
						indent
					/>
					<PlRow
						label="Marketing"
						value={Math.abs(opEx.marketing || 0)}
						cls="amber"
						indent
					/>
					<PlRow
						label="Miscellaneous"
						value={Math.abs(opEx.misc || 0)}
						cls="amber"
						indent
					/>
					<PlRow
						label="Total OpEx"
						value={Math.abs(opEx.total || 0)}
						cls="red"
						bold
						topB
					/>

					<div style={{ height: 14 }} />
					<PlRow
						label="NET PROFIT"
						value={inc.netProfit || 0}
						cls={(inc.netProfit || 0) >= 0 ? "green" : "red"}
						bold
						topB
					/>
					<div
						style={{
							background:
								(inc.netProfit || 0) >= 0
									? "rgba(34,197,94,.07)"
									: "rgba(239,68,68,.07)",
							borderRadius: 8,
							padding: "10px 12px",
							margin: "8px 12px 0",
						}}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								fontSize: 12,
							}}>
							<span style={{ color: "var(--muted)" }}>Net Margin</span>
							<span
								style={{
									fontWeight: 800,
									color:
										(inc.netProfit || 0) >= 0 ? "var(--accent)" : "var(--red)",
								}}>
								{inc.netMargin || "0%"}
							</span>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								fontSize: 12,
								marginTop: 4,
							}}>
							<span style={{ color: "var(--muted)" }}>Gross Margin</span>
							<span style={{ fontWeight: 800, color: "var(--amber)" }}>
								{inc.grossMargin || "0%"}
							</span>
						</div>
					</div>
				</div>

				{/* VAT + Cost structure */}
				<div>
					<div className="card">
						<div className="card-title">🏛️ VAT Summary</div>
						{[
							["Tax Rate", tax.rate || "15%"],
							["Net Revenue (taxable)", fmt(inc.netRevenue || 0)],
							["VAT Collected", fmt(tax.collected || 0)],
							["VAT Registration", tax.vatNumber || "—"],
						].map(([l, v]) => (
							<div
								key={l}
								style={{
									display: "flex",
									justifyContent: "space-between",
									padding: "9px 0",
									borderBottom: "1px solid rgba(26,43,29,.4)",
								}}>
								<span style={{ fontSize: 13, color: "var(--text2)" }}>{l}</span>
								<span style={{ fontSize: 13, fontWeight: 700 }}>{v}</span>
							</div>
						))}
					</div>

					<div className="card">
						<div className="card-title">💸 Cost Structure</div>
						{[
							[
								`COGS (${inc.netRevenue > 0 ? r1((Math.abs(inc.cogs || 0) / inc.netRevenue) * 100) : 0}%)`,
								Math.abs(inc.cogs || 0),
								"#ef4444",
							],
							[
								`Staff (${inc.netRevenue > 0 ? r1((Math.abs(opEx.staffCost || 0) / inc.netRevenue) * 100) : 0}%)`,
								Math.abs(opEx.staffCost || 0),
								"#f59e0b",
							],
							[
								`Rent (${inc.netRevenue > 0 ? r1((Math.abs(opEx.rent || 0) / inc.netRevenue) * 100) : 0}%)`,
								Math.abs(opEx.rent || 0),
								"#3b82f6",
							],
							[
								`Net Profit (${inc.netMargin || "0%"})`,
								inc.netProfit || 0,
								"#06a384",
							],
						].map(([l, v, c]) => {
							const nr = inc.netRevenue || 1;
							return (
								<div key={l} style={{ marginBottom: 12 }}>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: 4,
										}}>
										<span
											style={{
												fontSize: 12,
												color: "var(--text2)",
												fontWeight: 600,
											}}>
											{l}
										</span>
										<span style={{ fontSize: 12, fontWeight: 700, color: c }}>
											{fmt(Math.abs(v))}
										</span>
									</div>
									<div
										style={{
											height: 6,
											background: "var(--border2)",
											borderRadius: 99,
										}}>
										<div
											style={{
												height: "100%",
												width: `${Math.max(2, Math.min(100, (Math.abs(v) / nr) * 100))}%`,
												background: c,
												borderRadius: 99,
											}}
										/>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};
